/**
 * Costs as a share of revenue for the Property Portfolio Costs page
 * (operator request 2026-10-15: "what interests me is the % to revenue of
 * costs").
 *
 * Revenue = Booking Volume (gross booking revenue), the same base the
 * Overall dashboard and the monthly financials use for every margin %.
 *
 * The Costs page is a CASH view (operator decision 2026-09-03: "the cost
 * page is for cashflow not full p&l"), so the full mortgage instalment
 * (capital + interest) counts as a cost. Since 2026-10-25 the P&L is the
 * same cash view (Fan: "It should be cashview and therefore P&L needs to be
 * aligned"): mortgage capital rows are P&L-eligible and already sit inside
 * the financing figures, so they are carved out of "Rent" into their own
 * "Mortgage repayments" row rather than added again. Rows still flagged
 * `pnl_eligible: false` (older data) are added, as before.
 * Refundable deposits are NOT costs and stay out.
 *
 * Pure functions, native currency per region (GBP for UK, EUR for France).
 * Nothing here converts currency; the page converts for display.
 */

export const CASH_ONLY_COST_TYPES = new Set(['mortgage_principal']);

export const SHARE_ROWS = [
  { key: 'ota', label: 'OTA commission' },
  { key: 'management', label: 'Management fees' },
  { key: 'cleaning', label: 'Cleaning' },
  { key: 'repairs', label: 'Repairs and maintenance' },
  { key: 'other', label: 'Other running costs' },
  { key: 'financing', label: 'Rent' },
  { key: 'mortgage', label: 'Mortgage repayments' },
  { key: 'unclassified', label: 'Unclassified' },
];

const num = (v) => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const emptyBuckets = () => ({
  ota: 0, management: 0, cleaning: 0, repairs: 0, other: 0,
  financing: 0, mortgage: 0, unclassified: 0,
});

export const pctOf = (part, whole) => (whole > 0 ? (part / whole) * 100 : null);

/** Last month index (1-12) to include for `year`, given today. */
export function lastMonthFor(year, now = new Date()) {
  if (year < now.getFullYear()) return 12;
  if (year > now.getFullYear()) return 0;
  return now.getMonth() + 1;
}

/**
 * Cash-only cost instances (mortgage instalments) per region and month.
 * Returns { uk: {'YYYY-MM': amount}, fr: {...} } and per property totals.
 */
export function cashOnlyCosts(recurringCosts, properties, year, lastMonth) {
  const regionOf = {};
  for (const p of properties || []) regionOf[p.property_id] = p.region || 'uk';
  const byRegionMonth = { uk: {}, fr: {} };
  const byProperty = {};
  // Mortgage rows already inside the P&L financing figures (pnl_eligible).
  const inPnlByRegionMonth = { uk: {}, fr: {} };
  for (const c of recurringCosts || []) {
    if (!CASH_ONLY_COST_TYPES.has(c.cost_type)) continue;
    if (c.pnl_eligible !== false) {
      const mk2 = c.month || '';
      if (!mk2.startsWith(`${year}-`)) continue;
      const m2 = parseInt(mk2.slice(5, 7), 10);
      if (!(m2 >= 1 && m2 <= lastMonth)) continue;
      const r2 = regionOf[c.property_id];
      if (!r2 || !inPnlByRegionMonth[r2]) continue;
      inPnlByRegionMonth[r2][mk2] = (inPnlByRegionMonth[r2][mk2] || 0) + num(c.amount);
      continue;
    }
    const mk = c.month || '';
    if (!mk.startsWith(`${year}-`)) continue;
    const m = parseInt(mk.slice(5, 7), 10);
    if (!(m >= 1 && m <= lastMonth)) continue;
    const region = regionOf[c.property_id];
    if (!region || !byRegionMonth[region]) continue;
    const amt = num(c.amount);
    byRegionMonth[region][mk] = (byRegionMonth[region][mk] || 0) + amt;
    byProperty[c.property_id] = (byProperty[c.property_id] || 0) + amt;
  }
  return { byRegionMonth, byProperty, inPnlByRegionMonth };
}

/**
 * Region totals, monthly rows and per-property rows.
 *
 * monthlyByRegion: computeDerivedMetrics(...).monthlyByRegion
 *   (entries carry gross_revenue_<ccy>, ota_commission_<ccy>, ...).
 */
export function computeCostRevenueShare({ monthlyByRegion, recurringCosts, properties, year, now = new Date() }) {
  const lastMonth = lastMonthFor(year, now);
  const cash = cashOnlyCosts(recurringCosts, properties, year, lastMonth);
  const sfx = { uk: 'gbp', fr: 'eur' };

  const regions = {};
  const months = [];
  for (const region of ['uk', 'fr']) {
    const s = sfx[region];
    const buckets = emptyBuckets();
    let revenue = 0;
    const series = monthlyByRegion?.[region] || [];
    for (let i = 0; i < lastMonth; i++) {
      const e = series[i] || {};
      const mk = `${year}-${String(i + 1).padStart(2, '0')}`;
      const mRevenue = num(e[`gross_revenue_${s}`]);
      const mBuckets = {
        ota: num(e[`ota_commission_${s}`]),
        management: num(e[`management_fees_${s}`]),
        cleaning: num(e[`cleaning_${s}`]),
        repairs: num(e[`repairs_${s}`]),
        other: num(e[`other_operational_${s}`]),
        financing: num(e[`financing_${s}`]) - (cash.inPnlByRegionMonth[region][mk] || 0),
        mortgage: (cash.byRegionMonth[region][mk] || 0) + (cash.inPnlByRegionMonth[region][mk] || 0),
        unclassified: num(e[`unclassified_${s}`]),
      };
      // Operational adjustments (entity-level cleaning/ops not tied to one
      // property) live in operational_<ccy> but not in its sub-buckets;
      // carry any remainder into "other" so the rows add up to the total.
      const opSub = mBuckets.cleaning + mBuckets.repairs + mBuckets.other;
      const opGap = num(e[`operational_${s}`]) - opSub;
      if (Math.abs(opGap) > 0.005) mBuckets.other += opGap;

      const mTotal = Object.values(mBuckets).reduce((a, b) => a + b, 0);
      revenue += mRevenue;
      for (const k of Object.keys(buckets)) buckets[k] += mBuckets[k];
      if (!months[i]) months[i] = { month: mk, index: i };
      months[i][region] = { revenue: mRevenue, costs: mTotal, pct: pctOf(mTotal, mRevenue) };
    }
    const total = Object.values(buckets).reduce((a, b) => a + b, 0);
    regions[region] = {
      currency: region === 'fr' ? 'EUR' : 'GBP',
      revenue,
      buckets,
      total,
      pct: pctOf(total, revenue),
      left: revenue - total,
    };
  }

  const propertyRows = [];
  for (const p of properties || []) {
    let revenue = 0;
    let costs = 0;
    for (let m = 1; m <= lastMonth; m++) {
      const mk = `${year}-${String(m).padStart(2, '0')}`;
      const pm = p.pnl_by_month?.[mk];
      if (!pm) continue;
      revenue += num(pm.gross_revenue);
      costs += num(pm.total_costs);
    }
    costs += cash.byProperty[p.property_id] || 0;
    if (revenue === 0 && costs === 0) continue;
    propertyRows.push({
      property_id: p.property_id,
      name: p.name,
      region: p.region || 'uk',
      currency: (p.region || 'uk') === 'fr' ? 'EUR' : 'GBP',
      revenue,
      costs,
      pct: pctOf(costs, revenue),
    });
  }
  propertyRows.sort((a, b) => (b.pct ?? Infinity) - (a.pct ?? Infinity));

  return { lastMonth, regions, months, propertyRows };
}

export function formatPct(p) {
  if (p === null || p === undefined || !Number.isFinite(p)) return '\u2013';
  return `${p.toFixed(1)}%`;
}
